import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents
} from "react-leaflet";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const defaultCenter = [-21.7946, -48.1756];

const propertyIcon = L.divIcon({
  html: `
    <div style="
      width:34px;
      height:34px;
      border-radius:50%;
      background:#2563eb;
      color:white;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:18px;
      border:2px solid white;
      box-shadow:0 8px 22px rgba(0,0,0,0.35);
    ">
      🏠
    </div>
  `,
  className: "",
  iconSize: [34, 34],
  iconAnchor: [17, 17]
});

export default function CreateProperty() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [form, setForm] = useState({
    nome: "",
    bairro: "",
    cidade: "",
    endereco: "",
    latitude: "",
    longitude: "",
    valor_pago: "",
    valor_venda: "",
    usuarios: []
  });

  const [percentuais, setPercentuais] = useState({});

  const isMobile = screenWidth <= 700;
  const isTablet = screenWidth <= 1050;

  const hasLocation =
    form.latitude !== "" &&
    form.longitude !== "";

  const selectedPosition = hasLocation
    ? [Number(form.latitude), Number(form.longitude)]
    : null;

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    if (!me) return;

    const novo = calcularPercentuaisPadrao(
      me.id,
      form.usuarios
    );

    setPercentuais(novo);
  }, [me, form.usuarios]);

  const carregarDados = async () => {
    try {
      const meRes = await axios.get("/auth/me", {
        withCredentials: true
      });

      setMe(meRes.data);

      const usersRes = await axios.get("/auth/users", {
        withCredentials: true
      });

      setUsers(usersRes.data || []);
    } catch (err) {
      console.log(err);
      alert("Erro ao carregar usuários");
    }
  };

  const calcularPercentuaisPadrao = (creatorId, usuarios) => {
    const ids = [
      String(creatorId),
      ...(usuarios || []).map((id) => String(id))
    ];

    const unique = [...new Set(ids)];

    const totalParticipants = unique.length;

    if (totalParticipants === 1) {
      return {
        [unique[0]]: 100
      };
    }

    const percentualComum = Number(
      (90 / totalParticipants).toFixed(2)
    );

    const percentualCriador = Number(
      (percentualComum + 10).toFixed(2)
    );

    const result = {};

    unique.forEach((id) => {
      result[id] =
        String(id) === String(creatorId)
          ? percentualCriador
          : percentualComum;
    });

    const totalPercent = Object.values(result).reduce((sum, value) => {
      return sum + Number(value || 0);
    }, 0);

    const diff = Number((100 - totalPercent).toFixed(2));

    result[String(creatorId)] = Number(
      (Number(result[String(creatorId)] || 0) + diff).toFixed(2)
    );

    return result;
  };

  const update = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const montarEndereco = (address) => {
    const rua =
      address.road ||
      address.pedestrian ||
      address.footway ||
      address.path ||
      address.residential ||
      "";

    const numero =
      address.house_number ||
      "";

    const enderecoCompleto = [
      rua,
      numero
    ]
      .filter(Boolean)
      .join(", ");

    return enderecoCompleto;
  };

  const pegarBairro = (address) => {
    return (
      address.suburb ||
      address.neighbourhood ||
      address.quarter ||
      address.city_district ||
      address.district ||
      address.residential ||
      ""
    );
  };

  const pegarCidade = (address) => {
    return (
      address.city ||
      address.town ||
      address.village ||
      address.municipality ||
      address.county ||
      ""
    );
  };

  const buscarEnderecoPorCoordenadas = async (lat, lng) => {
    try {
      setLoadingAddress(true);

      const url =
        "https://nominatim.openstreetmap.org/reverse" +
        `?format=jsonv2` +
        `&lat=${lat}` +
        `&lon=${lng}` +
        `&addressdetails=1` +
        `&accept-language=pt-BR`;

      const res = await fetch(url);

      const data = await res.json();

      const address = data.address || {};

      const bairro = pegarBairro(address);
      const cidade = pegarCidade(address);
      const endereco = montarEndereco(address);

      setForm((prev) => ({
        ...prev,
        bairro: bairro || prev.bairro,
        cidade: cidade || prev.cidade,
        endereco:
          endereco ||
          data.display_name ||
          prev.endereco,
        latitude: Number(lat).toFixed(8),
        longitude: Number(lng).toFixed(8)
      }));
    } catch (err) {
      console.log(err);

      setForm((prev) => ({
        ...prev,
        latitude: Number(lat).toFixed(8),
        longitude: Number(lng).toFixed(8)
      }));

      alert("Local marcado, mas não foi possível buscar o endereço automaticamente");
    }

    setLoadingAddress(false);
  };

  const selecionarLocalizacao = async (lat, lng) => {
    setForm((prev) => ({
      ...prev,
      latitude: Number(lat).toFixed(8),
      longitude: Number(lng).toFixed(8)
    }));

    await buscarEnderecoPorCoordenadas(lat, lng);
  };

  const usarMinhaLocalizacao = () => {
    if (!navigator.geolocation) {
      alert("Seu navegador não permite pegar a localização atual");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        await selecionarLocalizacao(lat, lng);
      },
      () => {
        alert("Não foi possível pegar sua localização atual");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000
      }
    );
  };

  const limparLocalizacao = () => {
    setForm({
      ...form,
      latitude: "",
      longitude: ""
    });
  };

  const toggleUsuario = (id) => {
    const idString = String(id);

    if (form.usuarios.includes(idString)) {
      setForm({
        ...form,
        usuarios: form.usuarios.filter((item) => item !== idString)
      });
    } else {
      setForm({
        ...form,
        usuarios: [...form.usuarios, idString]
      });
    }
  };

  const updatePercentual = (id, value) => {
    setPercentuais({
      ...percentuais,
      [String(id)]: value
    });
  };

  const totalPercentual = Object.values(percentuais).reduce((sum, value) => {
    return sum + Number(value || 0);
  }, 0);

  const usuariosDisponiveis = users.filter((u) => {
    return String(u.id) !== String(me?.id);
  });

  const participantes = [
    ...(me
      ? [
          {
            ...me,
            isCreator: true
          }
        ]
      : []),
    ...users
      .filter((u) => form.usuarios.includes(String(u.id)))
      .map((u) => ({
        ...u,
        isCreator: false
      }))
  ];

  const salvar = async () => {
    try {
      if (!form.nome.trim()) {
        alert("Digite o nome do imóvel");
        return;
      }

      if (Math.abs(totalPercentual - 100) > 0.05) {
        alert("As porcentagens precisam somar 100%");
        return;
      }

      setLoading(true);

      const profitShares = Object.entries(percentuais).map(
        ([userId, percentual]) => {
          return {
            user_id: userId,
            percentual: Number(percentual) || 0
          };
        }
      );

      const res = await axios.post(
        "/properties/create",
        {
          nome: form.nome || "",
          bairro: form.bairro || "",
          cidade: form.cidade || "",
          endereco: form.endereco || "",
          latitude: form.latitude || null,
          longitude: form.longitude || null,
          valor_pago: Number(form.valor_pago) || 0,
          valor_venda: Number(form.valor_venda) || 0,
          usuarios: form.usuarios,
          profitShares
        },
        {
          withCredentials: true
        }
      );

      alert("Imóvel criado com sucesso");

      navigate(`/imovel/${res.data.propertyId}`);
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao criar imóvel";

      alert(msg);
    }

    setLoading(false);
  };

  return (
    <div
      style={{
        ...styles.page,
        padding: isMobile ? 14 : 28
      }}
    >
      <div style={styles.header}>
        <p style={styles.tag}>HITC imóveis</p>

        <h1
          style={{
            ...styles.title,
            fontSize: isMobile ? 26 : 34
          }}
        >
          Criar Imóvel
        </h1>

        <p style={styles.subtitle}>
          Cadastre o imóvel, escolha quem pode visualizar, defina a divisão do lucro e selecione o local no mapa.
        </p>
      </div>

      <div
        style={{
          ...styles.grid,
          gridTemplateColumns: isTablet ? "1fr" : "1fr 420px"
        }}
      >
        <div style={styles.leftCard}>
          <div
            style={{
              ...styles.formGrid,
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr"
            }}
          >
            <div
              style={{
                ...styles.field,
                gridColumn: isMobile ? "auto" : "span 2"
              }}
            >
              <label style={styles.label}>Nome do imóvel</label>

              <input
                name="nome"
                placeholder="Ex: Casa Village Damha"
                value={form.nome}
                onChange={update}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Bairro</label>

              <input
                name="bairro"
                placeholder={loadingAddress ? "Buscando bairro..." : "Ex: Village Damha"}
                value={form.bairro}
                onChange={update}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Cidade</label>

              <input
                name="cidade"
                placeholder={loadingAddress ? "Buscando cidade..." : "Ex: Araraquara"}
                value={form.cidade}
                onChange={update}
                style={styles.input}
              />
            </div>

            <div
              style={{
                ...styles.field,
                gridColumn: isMobile ? "auto" : "span 2"
              }}
            >
              <label style={styles.label}>Endereço</label>

              <input
                name="endereco"
                placeholder={loadingAddress ? "Buscando endereço..." : "Ex: Rua das Palmeiras, 123"}
                value={form.endereco}
                onChange={update}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Valor pago</label>

              <input
                name="valor_pago"
                type="number"
                placeholder="Ex: 420000"
                value={form.valor_pago}
                onChange={update}
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Valor de venda</label>

              <input
                name="valor_venda"
                type="number"
                placeholder="Ex: 550000"
                value={form.valor_venda}
                onChange={update}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.mapBox}>
            <div style={styles.usersHeader}>
              <div>
                <h3 style={styles.subTitle}>
                  Localização no mapa
                </h3>

                <p style={styles.subText}>
                  Clique no mapa para marcar o local exato. O bairro, cidade e endereço serão preenchidos automaticamente.
                </p>
              </div>

              {loadingAddress ? (
                <span style={styles.loadingBadge}>
                  Buscando endereço...
                </span>
              ) : hasLocation ? (
                <span style={styles.counter}>
                  Local marcado
                </span>
              ) : null}
            </div>

            <div style={styles.mapWrapper}>
              <MapContainer
                center={selectedPosition || defaultCenter}
                zoom={selectedPosition ? 16 : 13}
                style={styles.leafletMap}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap"
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapClickHandler
                  onSelect={selecionarLocalizacao}
                />

                {selectedPosition && (
                  <>
                    <MapViewUpdater center={selectedPosition} />

                    <Marker
                      position={selectedPosition}
                      icon={propertyIcon}
                    >
                      <Popup>
                        Local do imóvel
                      </Popup>
                    </Marker>
                  </>
                )}
              </MapContainer>
            </div>

            <div style={styles.mapActions}>
              <button
                onClick={usarMinhaLocalizacao}
                type="button"
                style={styles.secondaryButton}
              >
                Usar minha localização
              </button>

              <button
                onClick={limparLocalizacao}
                type="button"
                style={styles.dangerSoftButton}
              >
                Limpar localização
              </button>
            </div>

            <div style={styles.coordGrid}>
              <input
                name="latitude"
                placeholder="Latitude"
                value={form.latitude}
                onChange={update}
                style={styles.input}
              />

              <input
                name="longitude"
                placeholder="Longitude"
                value={form.longitude}
                onChange={update}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.usersBox}>
            <div style={styles.usersHeader}>
              <div>
                <h3 style={styles.subTitle}>
                  Permissão de visualização
                </h3>

                <p style={styles.subText}>
                  Marque os usuários que poderão acessar a página desse imóvel.
                </p>
              </div>

              <span style={styles.counter}>
                {form.usuarios.length} selecionado(s)
              </span>
            </div>

            <div
              style={{
                ...styles.usersGrid,
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr"
              }}
            >
              {usuariosDisponiveis.map((u) => (
                <label
                  key={u.id}
                  style={{
                    ...styles.userRow,
                    border:
                      form.usuarios.includes(String(u.id))
                        ? "1px solid rgba(37,99,235,0.8)"
                        : "1px solid rgba(255,255,255,0.06)",
                    background:
                      form.usuarios.includes(String(u.id))
                        ? "rgba(37,99,235,0.12)"
                        : "#020617"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.usuarios.includes(String(u.id))}
                    onChange={() => toggleUsuario(u.id)}
                  />

                  <img
                    src={u.foto || fallback}
                    alt={u.nome}
                    style={styles.avatar}
                  />

                  <div style={styles.userInfo}>
                    <div style={styles.userName}>
                      {u.nome}
                    </div>

                    <div style={styles.userEmail}>
                      {u.email}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div style={styles.profitBox}>
            <div style={styles.usersHeader}>
              <div>
                <h3 style={styles.subTitle}>
                  Divisão do lucro
                </h3>

                <p style={styles.subText}>
                  O criador começa com 10 pontos percentuais a mais, mas você pode ajustar manualmente.
                </p>
              </div>

              <span
                style={{
                  ...styles.percentTotal,
                  color:
                    Math.abs(totalPercentual - 100) <= 0.05
                      ? "#86efac"
                      : "#fca5a5",
                  background:
                    Math.abs(totalPercentual - 100) <= 0.05
                      ? "rgba(34,197,94,0.12)"
                      : "rgba(239,68,68,0.12)"
                }}
              >
                Total: {totalPercentual.toFixed(2)}%
              </span>
            </div>

            <div style={styles.profitList}>
              {participantes.map((u) => (
                <div key={u.id} style={styles.profitRow}>
                  <div style={styles.profitUser}>
                    <img
                      src={u.foto || fallback}
                      alt={u.nome}
                      style={styles.avatar}
                    />

                    <div>
                      <div style={styles.userName}>
                        {u.nome} {u.isCreator ? "(Criador)" : ""}
                      </div>

                      <div style={styles.userEmail}>
                        {u.email}
                      </div>
                    </div>
                  </div>

                  <input
                    type="number"
                    value={percentuais[String(u.id)] ?? ""}
                    onChange={(e) => updatePercentual(u.id, e.target.value)}
                    style={styles.percentInput}
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={salvar}
            disabled={loading || loadingAddress}
            style={{
              ...styles.button,
              opacity: loading || loadingAddress ? 0.7 : 1,
              cursor: loading || loadingAddress ? "not-allowed" : "pointer"
            }}
          >
            {loading
              ? "Salvando..."
              : loadingAddress
                ? "Aguarde o endereço..."
                : "Criar página do imóvel"}
          </button>
        </div>

        <div style={styles.rightCard}>
          <div
            style={{
              ...styles.previewImage,
              height: isMobile ? 220 : 300
            }}
          >
            <div style={styles.houseIcon}>
              🏠
            </div>

            <div style={styles.defaultText}>
              Imagem padrão
            </div>
          </div>

          <div style={styles.previewContent}>
            <p style={styles.previewTag}>
              Prévia do imóvel
            </p>

            <h2 style={styles.previewTitle}>
              {form.nome || "Nome do imóvel"}
            </h2>

            <p style={styles.previewLocation}>
              {(form.bairro || "Bairro") + " • " + (form.cidade || "Cidade")}
            </p>

            <div style={styles.previewDivider} />

            <div style={styles.previewRow}>
              <span>Valor pago</span>
              <strong>{formatMoney(form.valor_pago)}</strong>
            </div>

            <div style={styles.previewRow}>
              <span>Valor de venda</span>
              <strong>{formatMoney(form.valor_venda)}</strong>
            </div>

            <div style={styles.previewRow}>
              <span>Participantes do lucro</span>
              <strong>{participantes.length}</strong>
            </div>

            <div style={styles.previewRow}>
              <span>Localização</span>
              <strong>{hasLocation ? "Marcada" : "Não marcada"}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MapClickHandler({ onSelect }) {
  useMapEvents({
    click(e) {
      onSelect(e.latlng.lat, e.latlng.lng);
    }
  });

  return null;
}

function MapViewUpdater({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.setView(center, 16);
    }
  }, [center, map]);

  return null;
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#0f172a",
    color: "#fff",
    boxSizing: "border-box"
  },

  header: {
    marginBottom: 24
  },

  tag: {
    color: "#60a5fa",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 8
  },

  title: {
    margin: 0,
    color: "#f8fafc",
    fontWeight: 800
  },

  subtitle: {
    marginTop: 8,
    color: "#94a3b8",
    maxWidth: 700,
    lineHeight: 1.5
  },

  grid: {
    display: "grid",
    gap: 22,
    alignItems: "start",
    width: "100%"
  },

  leftCard: {
    background: "rgba(15,23,42,0.96)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    padding: 22,
    boxSizing: "border-box"
  },

  rightCard: {
    background: "linear-gradient(180deg, #111827, #020617)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 22,
    overflow: "hidden",
    boxShadow: "0 24px 70px rgba(0,0,0,0.35)"
  },

  formGrid: {
    display: "grid",
    gap: 14
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7
  },

  label: {
    color: "#cbd5e1",
    fontSize: 13
  },

  input: {
    width: "100%",
    padding: 14,
    borderRadius: 12,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box"
  },

  mapBox: {
    marginTop: 22,
    padding: 18,
    borderRadius: 18,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.07)"
  },

  mapWrapper: {
    width: "100%",
    height: 360,
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "#020617"
  },

  leafletMap: {
    width: "100%",
    height: "100%"
  },

  mapActions: {
    display: "flex",
    gap: 10,
    marginTop: 12,
    flexWrap: "wrap"
  },

  secondaryButton: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.16)",
    color: "#bfdbfe",
    cursor: "pointer"
  },

  dangerSoftButton: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(239,68,68,0.28)",
    background: "rgba(239,68,68,0.10)",
    color: "#fecaca",
    cursor: "pointer"
  },

  coordGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 12
  },

  usersBox: {
    marginTop: 22,
    padding: 18,
    borderRadius: 18,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.07)"
  },

  profitBox: {
    marginTop: 22,
    padding: 18,
    borderRadius: 18,
    background: "#111827",
    border: "1px solid rgba(255,255,255,0.07)"
  },

  usersHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    marginBottom: 14,
    flexWrap: "wrap"
  },

  subTitle: {
    color: "#fff",
    fontSize: 17,
    margin: 0
  },

  subText: {
    color: "#94a3b8",
    fontSize: 13,
    marginTop: 6,
    marginBottom: 0
  },

  counter: {
    background: "rgba(37,99,235,0.15)",
    color: "#93c5fd",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  loadingBadge: {
    background: "rgba(234,179,8,0.14)",
    color: "#fde68a",
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  percentTotal: {
    padding: "7px 10px",
    borderRadius: 999,
    fontSize: 12,
    whiteSpace: "nowrap"
  },

  usersGrid: {
    display: "grid",
    gap: 10
  },

  userRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 14,
    cursor: "pointer",
    color: "#fff",
    boxSizing: "border-box"
  },

  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0
  },

  userInfo: {
    minWidth: 0
  },

  userName: {
    fontSize: 14,
    color: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  userEmail: {
    fontSize: 12,
    color: "#94a3b8",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },

  profitList: {
    display: "flex",
    flexDirection: "column",
    gap: 10
  },

  profitRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    borderRadius: 14,
    background: "#020617",
    border: "1px solid rgba(255,255,255,0.06)"
  },

  profitUser: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0
  },

  percentInput: {
    width: 90,
    padding: 10,
    borderRadius: 10,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    textAlign: "center"
  },

  button: {
    width: "100%",
    marginTop: 22,
    padding: 15,
    border: "none",
    borderRadius: 14,
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold",
    fontSize: 15
  },

  previewImage: {
    width: "100%",
    background:
      "linear-gradient(135deg, #111827 0%, #1e293b 45%, #0f172a 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    color: "#64748b"
  },

  houseIcon: {
    fontSize: 58,
    marginBottom: 8
  },

  defaultText: {
    fontSize: 14
  },

  previewContent: {
    padding: 22
  },

  previewTag: {
    color: "#60a5fa",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    margin: 0
  },

  previewTitle: {
    margin: "8px 0 0",
    fontSize: 24,
    color: "#f8fafc"
  },

  previewLocation: {
    color: "#94a3b8",
    marginTop: 8,
    fontSize: 14
  },

  previewDivider: {
    height: 1,
    background: "rgba(255,255,255,0.08)",
    margin: "18px 0"
  },

  previewRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 0",
    color: "#cbd5e1",
    fontSize: 14
  }
};